import React, { useState, useEffect } from "react";
import { CalendarIcon, ClockIcon, MapPinIcon, ArrowLeft } from "lucide-react";
import ProductCard from "../../../Product/ProductCard.jsx";
import Loader from "../../../Loader/Loader.jsx";
import Cookies from "js-cookie";
import { useParams, useHistory } from "react-router-dom";
import useEventStore from "../../../../Store/EventStore.jsx";
import useAppStore from "../../../../Store/SearchStore.jsx";
import { EditGiftFromEventOrSpaceAPI } from "../../../../Services/eventService.js";
import EditableField from "../../../ui/EditableField.jsx";
import EditableTextarea from "../../../ui/EditableTextarea.jsx";
import IconButton from "../../../ui/Button/IconButton.jsx";
import { useTranslation } from "react-i18next";

export default function Component() {
  const { t } = useTranslation();
  const [selectedEventDetails, setSelectedEventDetails] = useState("");
  const { eventName } = useParams();
  const history = useHistory();
  const type = "events";

  const {
    setSearchText,
    getSuggestedGifts,
    gifts: suggestedGifts,
    isSearching,
  } = useAppStore();

  const { gifts, fetchEventOrSpaceDetails } = useEventStore();
  const [loading, setLoading] = useState(true);

  const giftTitles = gifts.map((gift) => gift.title).join(", ") || "";
  const rawGiftTitles = gifts.map((gift) => gift.title);
  const FilteredSuggestedProducts = suggestedGifts.filter(
    (suggestedGift) => !rawGiftTitles.includes(suggestedGift.title)
  );

  const [eventDetails, setEventDetails] = useState({
    title: t("eventsAndSpaces.eventName"),
    date: new Date(),
    time: new Date("2024-07-20T12:00:00"),
    location: t("eventsAndSpaces.location"),
    description: t("gift.description"),
    category: t("eventsAndSpaces.category"),
    coverImage: {
      src: "/placeholder.svg?height=400&width=800&text=Event+Image",
      position: { x: 0, y: 0 },
    },
  });

  useEffect(() => {
    if (selectedEventDetails) {
      setEventDetails({
        title: selectedEventDetails.eventName || t("eventsAndSpaces.eventName"),
        date: selectedEventDetails.eventDate
          ? new Date(selectedEventDetails.eventDate)
          : new Date(),
        time: selectedEventDetails.eventTime
          ? new Date(`2024-07-20T${selectedEventDetails.eventTime}:00`)
          : new Date("2024-07-20T12:00:00"),
        location: selectedEventDetails.eventLocation || t("eventsAndSpaces.location"),
        description: selectedEventDetails.eventDescription || t("gift.description"),
        category: selectedEventDetails.category || t("eventsAndSpaces.category"),
        coverImage: {
          src:
            selectedEventDetails.eventImage ||
            "/placeholder.svg?height=400&width=800&text=Event+Image",
          position: { x: 0, y: 0 },
        },
      });
    }
  }, [selectedEventDetails, t]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await fetchEventOrSpaceDetails(type, eventName);
      setSelectedEventDetails(data);
      setLoading(false);
    };
    fetchData();
  }, [eventName, type, fetchEventOrSpaceDetails]);

  useEffect(() => {
    const cookie = Cookies.get();
    getSuggestedGifts(
      cookie,
      giftTitles,
      eventName,
      eventDetails.description ? eventDetails.description : " "
    );
    setSearchText(localStorage.getItem("searchText") || "");
  }, [
    giftTitles,
    eventName,
    setSearchText,
    eventDetails.description,
    getSuggestedGifts
  ]);

  const updateEventDetail = async (key, value) => {
    const newEventDetails = { ...eventDetails, [key]: value };
    setEventDetails(newEventDetails);

    const keyMap = {
      title: "eventName",
      date: "eventDate",
      time: "eventTime",
      location: "eventLocation",
      description: "eventDescription",
      category: "eventCategory",
    };

    const apiPayload = Object.entries(newEventDetails).reduce(
      (acc, [localKey, localValue]) => {
        const apiKey = keyMap[localKey] || localKey;
        acc[apiKey] =
          localKey === "time"
            ? localValue.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })
            : localValue;
        return acc;
      },
      {}
    );

    try {
      const { coverImage, ...payloadWithoutImage } = apiPayload;
      const response = await EditGiftFromEventOrSpaceAPI(
        "events",
        eventName,
        payloadWithoutImage
      );
      if (response) {
        if (key === "title") {
          history.replace(`/events/${value}`);
        }
      }
    } catch (error) {
      console.error("Error updating event details:", error);
    }
  };

  const handlePromptValidation = (giftID) => {
    let prompt = null;
    if (Array.isArray(selectedEventDetails.giftID)) {
      for (let ids of selectedEventDetails.giftID) {
        if (Array.isArray(ids) && ids.includes(giftID)) {
          prompt = ids[1] || null;
          break;
        }
      }
    }
    return prompt;
  };

  return (
    <div className="min-h-screen bg-grey-500">
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center items-center">
            <Loader className="h-8 w-8" />
          </div>
        ) : (
          <>
            <div className="container space-y-4">
              <EditableField
                initialValue={eventDetails.title}
                onSave={(value) => updateEventDetail("title", value)}
                className="text-3xl font-bold text-red-500"
              />
              <div>
                <EditableTextarea
                  initialValue={eventDetails.description}
                  onSave={(value) => updateEventDetail("description", value)}
                />
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <div className="flex items-center">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  <EditableField
                    initialValue={eventDetails.date}
                    onSave={(value) => updateEventDetail("date", value)}
                    type="date"
                  />
                </div>
                <div className="flex items-center">
                  <ClockIcon className="mr-2 h-4 w-4" />
                  <EditableField
                    initialValue={eventDetails.time}
                    onSave={(value) => updateEventDetail("time", value)}
                    type="time"
                  />
                </div>
                <div className="flex items-center">
                  <MapPinIcon className="mr-2 h-4 w-4" />
                  <EditableField
                    initialValue={eventDetails.location}
                    onSave={(value) => updateEventDetail("location", value)}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center sm:flex-wrap sm:justify-between my-10">
              <h2 className="text-2xl font-semibold mb-4">{t("eventsAndSpaces.savedProductsTitle")}</h2>
              <div className="flex flex-col items-end sm:flex-row sm:flex-wrap sm:justify-evenly">
                {!gifts || gifts.length === 0 ? (
                  <p className="text-gray-500">
                    {t("eventsAndSpaces.noGiftsSaved")}
                  </p>
                ) : (
                  gifts.map((gift) => (
                    <ProductCard
                      variant="event"
                      key={gift._id}
                      id={gift._id}
                      title={gift.title}
                      image={gift.img}
                      url={gift.link}
                      price={gift.price}
                      country={gift.country}
                      description={gift.description}
                      explanation={gift.explanation}
                      lovemeter={gift.loveMeter}
                      rank={gift.rank}
                      // categories={gift.categories}
                      // categoriesTranslated={gift.categoriesTranslated}
                      name={eventDetails.title}
                      type="events"
                      heart="delete"
                      prompt={handlePromptValidation(gift._id)}
                    />
                  ))
                )}
              </div>
            </div>

            <div className="bg-green-100 p-4 rounded-xl">
              <h2 className="text-2xl font-semibold">{t("eventsAndSpaces.suggestedProductsTitle")}</h2>
              {isSearching ? (
                <div className="flex justify-center items-center ">
                  <Loader className="h-8 w-8" />
                </div>
              ) : (
                <div className="flex flex-nowrap items-center justify-between overflow-x-auto gap-4 mt-3">
                  {FilteredSuggestedProducts &&
                    FilteredSuggestedProducts.map((gift) => (
                      <ProductCard
                        variant="event"
                        key={gift._id}
                        id={gift._id}
                        title={gift.title}
                        image={gift.img}
                        url={gift.link}
                        price={gift.price}
                        country={gift.country}
                        description={gift.description}
                        explanation={gift.explanation}
                        lovemeter={gift.loveMeter}
                        rank={gift.rank}
                        // categories={gift.categories}
                        // categoriesTranslated={gift.categoriesTranslated}
                        name={eventDetails.title}
                        type="events"
                      />
                    ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
